import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Alert,
  Pagination,
  Divider,
  Chip,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Edit, Delete, CheckCircle, Archive, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useIssueManagement } from '../hooks/useIssueManagement';
import { IssueForm } from './IssueForm';

const ITEMS_PER_PAGE = 4;

export const IssuesTab: React.FC = () => {
  const {
    issues,
    issuesLoading,
    issuesError,
    issueFormOpen,
    formMode,
    selectedIssue,
    setIssueFormOpen,
    handleAddIssue,
    handleEditIssue,
    handleDeleteIssue,
    handleIssueSubmit,
    handlePublishIssue,
    handleArchiveIssue,
  } = useIssueManagement();

  const [page, setPage] = useState(1);
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const years = useMemo(() => {
    const uniqueYears = new Set(issues.map(issue => issue.year));
    return Array.from(uniqueYears).sort((a, b) => b - a);
  }, [issues]);

  const filteredIssues = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return issues.filter((issue) => {
      if (selectedYear !== 'all' && issue.year.toString() !== selectedYear) {
        return false;
      }
      if (!query) return true;

      return (
        issue.title?.toLowerCase().includes(query) ||
        issue.description?.toLowerCase().includes(query) ||
        String(issue.year).includes(query) ||
        issue.status?.toLowerCase().includes(query)
      );
    });
  }, [issues, selectedYear, searchQuery]);

  const paginatedIssues = filteredIssues.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleYearChange = (event: SelectChangeEvent) => {
    setSelectedYear(event.target.value);
    setPage(1);
  };

  const getStatusChip = (status: string | undefined) => {
    if (!status) return <Chip label="Неизвестно" />;

    switch (status) {
      case 'draft':
        return <Chip label="Черновик" color="default" />;
      case 'published':
        return <Chip label="Опубликовано" color="success" />;
      case 'archived':
        return <Chip label="Архивировано" color="default" />;
      default:
        return <Chip label={status} />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
        mb: 3,
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flex: 1 }}>
          <TextField
            size="small"
            placeholder="Поиск по названию, описанию, году…"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 260, flex: 1, maxWidth: 420 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Фильтр по году</InputLabel>
            <Select
              value={selectedYear}
              label="Фильтр по году"
              onChange={handleYearChange}
            >
              <MenuItem value="all">Все годы</MenuItem>
              {years.map((year) => (
                <MenuItem key={year} value={year.toString()}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Button 
          variant="contained" 
          onClick={handleAddIssue}
          startIcon={<AddIcon />}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            px: 3
          }}
        >
          Добавить выпуск
        </Button>
      </Box>

      {issuesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : issuesError ? (
        <Alert severity="error" sx={{ mb: 2 }}>{issuesError}</Alert>
      ) : (
        <>
          <Paper elevation={0} sx={{ 
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            <List sx={{ p: 0 }}>
              {paginatedIssues.length === 0 ? (
                <ListItem>
                  <ListItemText primary="Ничего не найдено" />
                </ListItem>
              ) : (
                paginatedIssues.map((issue) => (
                  <React.Fragment key={issue.id}>
                    <ListItem
                      sx={{
                        py: 2,
                        px: 3,
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography component="div" variant="subtitle1" sx={{ fontWeight: 500 }}>
                              {issue.title}
                            </Typography>
                            {getStatusChip(issue.status)}
                          </Box>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary">
                            {issue.description}
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton 
                            edge="end" 
                            onClick={() => handleEditIssue(issue)}
                            sx={{ 
                              color: 'primary.main',
                              '&:hover': { backgroundColor: 'primary.lighter' }
                            }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton 
                            edge="end" 
                            onClick={() => handleDeleteIssue(issue.id)}
                            sx={{ 
                              color: 'error.main',
                              '&:hover': { backgroundColor: 'error.lighter' }
                            }}
                          >
                            <Delete />
                          </IconButton>
                          {issue.status === 'draft' && (
                            <IconButton 
                              edge="end" 
                              onClick={() => handlePublishIssue(issue.id)}
                              sx={{ 
                                color: 'success.main',
                                '&:hover': { backgroundColor: 'success.lighter' }
                              }}
                            >
                              <CheckCircle />
                            </IconButton>
                          )}
                          {issue.status === 'published' && (
                            <IconButton 
                              edge="end" 
                              onClick={() => handleArchiveIssue(issue.id)}
                              sx={{ 
                                color: 'info.main',
                                '&:hover': { backgroundColor: 'info.lighter' }
                              }}
                            >
                              <Archive />
                            </IconButton>
                          )}
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))
              )}
            </List>
          </Paper>

          {filteredIssues.length > ITEMS_PER_PAGE && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={Math.ceil(filteredIssues.length / ITEMS_PER_PAGE)}
                page={page}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: 1
                  }
                }}
              />
            </Box>
          )}
        </>
      )}

      <IssueForm
        open={issueFormOpen}
        onClose={() => setIssueFormOpen(false)}
        onSubmit={handleIssueSubmit}
        initialData={selectedIssue || undefined}
        mode={formMode}
        isLoading={issuesLoading}
      />
    </Box>
  );
}; 